import { ContentBoxTitle } from "../../../components/common";
import { Stack, Typography } from "@mui/material";
import {
  FormAutoComplete,
  FormButtonGroup,
  FormMultipleAutoComplete,
  FormSelectInput,
  FormTextInput,
} from "../../../components/form";
import { useNavigate } from "react-router-dom";
import { UFValueSet } from "./form/ValueSet";
import { IFormInput } from "../Types";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  GetPractitionerRole,
  UpdatePractitionerRole,
} from "../../../services/fhir";
import { useSessionContext } from "../../../contexts/SessionContext";
import { currentFeatures, oids } from "../../../configs/Settings";
import { toast } from "react-toastify";
import { useWatch, UseFormSetValue } from "react-hook-form";
import { useValueSet } from "../../../hooks/useValueSet";
import {
  CONSELHOS_SAUDE_VALUESET_ID,
  getConselhosSaudeUrl,
} from "../cboValueSetByConselho";

interface IProps {
  control: any;
  onNextButtonClick: Function;
  specialties: {
    label: string;
    value: string;
    display: string;
  }[];
  loading: boolean;
  edit?: boolean;
  setSearchTerm?: any;
  /** Usado para limpar especialidade ao trocar o conselho */
  setValue?: UseFormSetValue<any>;
}

/** Fallback Fluxvet — mantém lista fixa quando !defaultApp */
const crmv = [
  {
    display: "CRMV - Conselho Regional de Medicina Veterinária",
    value: "CRMV",
    descricao: "Medicina Veterinária",
    code: 1,
  },
];

export default function ProfessionalForm({
  control,
  onNextButtonClick,
  specialties,
  loading,
  edit,
  setSearchTerm,
  setValue,
}: IProps) {
  const navigate = useNavigate();
  const [Newloading, setNewloading] = useState(false);
  const { user, access } = useSessionContext();

  // Conselhos via terminologia r5 (só no app de saúde; Fluxvet segue com crmv)
  const { options: conselhosFromValueSet, loading: conselhosLoading } =
    useValueSet({
      valueSet: currentFeatures.defaultApp ? CONSELHOS_SAUDE_VALUESET_ID : "",
      version: "r5",
      // Canonical em fluxmed.cloud (r5) — GetValueSet com version usa fluxmed.com.br
      customUrl: currentFeatures.defaultApp
        ? getConselhosSaudeUrl()
        : undefined,
    });

  const conselhosOptions = useMemo(
    () =>
      conselhosFromValueSet?.map((item) => ({
        display: item.display,
        value: item.code,
        code: item.code,
      })) ?? [],
    [conselhosFromValueSet],
  );

  const options = currentFeatures.defaultApp ? conselhosOptions : crmv;

  const selectedOption = useWatch({ name: "classe", control });
  // Controla o texto do filtro — necessário para digitar com value=[] e busca remota
  const [specialtyInput, setSpecialtyInput] = useState("");
  // Evita limpar especialidade no mount (ex.: edição com valores já carregados)
  const previousClasseRef = useRef<string | undefined>(undefined);

  // Ao trocar conselho: limpa especialidade e termo de busca para refetch do CBO certo
  useEffect(() => {
    const previous = previousClasseRef.current;
    previousClasseRef.current = selectedOption;

    if (previous === undefined || previous === selectedOption) return;

    setValue?.("speciality", currentFeatures.defaultApp ? [] : "");
    setSpecialtyInput("");
    setSearchTerm?.("");
  }, [selectedOption, setValue, setSearchTerm]);

  const onSubmit = async (data: IFormInput) => {
    setNewloading(true);

    try {
      const practitionerRole = await GetPractitionerRole({
        id: `${access?.organizationId}-${oids.cpf}-${user.username}`,
      });

      await UpdatePractitionerRole(practitionerRole, {
        conselho: data?.classe,
        numero: data?.number,
        uf: data?.uf,
        speciality: currentFeatures.defaultApp
          ? data?.speciality ||
            options?.find((e: any) => e.value === selectedOption)
          : [
              {
                value: "1",
                label: data?.speciality || "Veterinária",
                display: data?.speciality || "Veterinária",
              },
            ],
      });

      toast.success("Registro profissional realizado com sucesso!");
      navigate("/");
      window.location.reload();
    } catch (err) {
      console.log("err", err);
    } finally {
      setNewloading(false);
    }
  };

  return (
    <Stack
      width="100%"
      height={{ xs: "fit-content", md: "100%" }}
      flex={"1"}
      justifyContent={{ xs: "flex-start", md: "center" }}
      alignItems="center"
      spacing={4}
    >
      {!edit && (
        <>
          <ContentBoxTitle>Informações profissionais</ContentBoxTitle>
          <Typography
            variant="body1"
            color="neutral700.main"
            textAlign="center"
            m={{ xs: 2, md: 5 }}
          >
            Insira seu conselho de classe e UF para finalizar seu cadastro como
            profissional de saúde
          </Typography>
        </>
      )}

      <Stack direction="row" width="100%" spacing={2}>
        <FormSelectInput
          control={control}
          name="classe"
          label="Conselho de classe"
          selectOptions={options}
          required
        />

        <Stack width="40%">
          <FormAutoComplete
            name="uf"
            control={control}
            label="UF"
            selectOptions={UFValueSet}
            required
          />
        </Stack>
      </Stack>

      <FormTextInput
        name="number"
        maxLength={selectedOption === "CRM" ? 8 : undefined}
        control={control}
        label="Número no conselho de classe"
        required={true}
        autoFocus
      />

      {currentFeatures.defaultApp ? (
        <FormMultipleAutoComplete
          variant="filled"
          name="speciality"
          control={control}
          defaultValue={[]}
          inputValue={specialtyInput}
          noOptionsText={
            selectedOption
              ? "Digite para buscar especialidade"
              : "Selecione o conselho de classe primeiro"
          }
          onInputChange={(
            _: React.SyntheticEvent,
            value: string,
            reason: string,
          ) => {
            // MUI Autocomplete passa (event, value, reason)
            if (reason === "input") {
              setSpecialtyInput(value);
              setSearchTerm?.(value);
            } else if (reason === "reset" || reason === "clear") {
              setSpecialtyInput("");
              setSearchTerm?.("");
            }
          }}
          label="Especialidade - CBO (Classe Brasileira de Ocupações)"
          selectOptions={specialties}
          // loading={loading || conselhosLoading}
          required
        />
      ) : (
        <FormTextInput
          name="speciality"
          control={control}
          label="Especialidade"
          required
        />
      )}

      {!edit && (
        <Stack
          width="100%"
          justifyContent="flex-end"
          direction="row"
          spacing={3}
          mt={2}
        >
          <FormButtonGroup
            onGoBackButtonClick={() => navigate("/")}
            onNextButtonClick={onNextButtonClick(onSubmit)}
            goBackButtonText="Voltar"
            nextButtonText="Finalizar"
            loading={Newloading || conselhosLoading}
          />
        </Stack>
      )}
    </Stack>
  );
}
